/* eslint-disable @typescript-eslint/no-shadow */
import {
  LeasePriceHistoryType,
  toCalendarDate,
  type CalendarDate,
  type ContactType,
  type Filter,
  type FilterMinMax,
} from '@rentguru/commons-utils';
import { getCacheItem, setCacheItem } from '@up2rent/graphql';
import isNil from 'lodash/isNil';
import React, { useContext, useEffect, useState } from 'react';

import type { DocumentGrouping } from 'src/components/Accounting/Transactions/Reconciliation/detailedReconciliationUtils';

export type { FilterMinMax };

export interface FilterFromTo {
  from: CalendarDate | null;
  to: CalendarDate | null;
}

export interface CommonFilter {
  filters: Filter[];
}

interface SearchFilter {
  search: string | null;
}

export enum CommunicationGroupByOption {
  LEASE = 'LEASE',
  RECIPIENT = 'RECIPIENT',
  TYPE = 'TYPE',
}

export interface CommunicationsFilters extends CommonFilter, SearchFilter {
  communicationsFromTo: FilterFromTo | null;
  groupBy: CommunicationGroupByOption | null;
}

export interface ContactsFilters extends CommonFilter, SearchFilter {
  contactTypes: ContactType[];
}

export enum ManagementFiltersFiltersKey {
  CITIES = 'cities',
  UNIT_TYPES = 'unitTypes',
  SEARCH = 'search',
  SHOW_UNASSIGNED_ENTITIES = 'showUnassignedEntities',
}
export interface ManagementFiltersFilters extends CommonFilter, SearchFilter {
  showUnassignedEntities: boolean;
}

export enum PropertiesFiltersAvailabilityName {
  VACANT,
  OWN_USE,
  RENOVATION,
  RENTED,
}

export interface PropertiesFilters extends CommonFilter, SearchFilter {
  budget: FilterMinMax | null;
  rooms: FilterMinMax | null;
  availabilitySelected: PropertiesFiltersAvailabilityName | null;
  availabilityFromTo: FilterFromTo;
  allBuildingsOpen: boolean;
  collapsedGroupIds: string[];
  unitsOpenByBuildingId: Record<string, boolean>;
}

export enum LeaseDatePreset {
  THIS_MONTH = 'THIS_MONTH',
  NEXT_MONTH = 'NEXT_MONTH',
  THIS_YEAR = 'THIS_YEAR',
  LAST_YEAR = 'LAST_YEAR',
}

export interface LeaseDateFilter {
  preset: LeaseDatePreset | null;
  from?: CalendarDate;
  to?: CalendarDate;
}

export enum LeaseDurationOption {
  UNDER_ONE_YEAR = 'UNDER_ONE_YEAR',
  ONE_TO_THREE_YEARS = 'ONE_TO_THREE_YEARS',
  THREE_TO_NINE_YEARS = 'THREE_TO_NINE_YEARS',
  NINE_YEARS_AND_MORE = 'NINE_YEARS_AND_MORE',
}

export enum LeaseGroupByOption {
  START_MONTH = 'START_MONTH',
  START_YEAR = 'START_YEAR',
  END_MONTH = 'END_MONTH',
  END_YEAR = 'END_YEAR',
  BUILDING = 'BUILDING',
  OWNER = 'OWNER',
  CITY = 'CITY',
  TYPE = 'TYPE',
}

export interface LeasesCommonFilters extends CommonFilter, SearchFilter {
  startDateFilter: LeaseDateFilter;
  durations: LeaseDurationOption[];
  subletting: boolean | null;
  groupBy: LeaseGroupByOption | null;
}

const REMOVED_LEASE_CLASSIC_FILTER_NAME = 'lease';

const rehydrateCalendarDate = (value: CalendarDate | string | null | undefined): CalendarDate | null => {
  if (isNil(value)) {
    return null;
  }
  return typeof value === 'string' ? toCalendarDate(value) : value;
};

const rehydrateFilterFromTo = (fromTo: FilterFromTo | null | undefined): FilterFromTo | null => {
  if (isNil(fromTo)) {
    return null;
  }
  return { from: rehydrateCalendarDate(fromTo.from), to: rehydrateCalendarDate(fromTo.to) };
};

const rehydrateLeaseDatePreset = (preset: LeaseDatePreset | null | undefined): LeaseDatePreset | null => {
  if (isNil(preset)) {
    return null;
  }
  return Object.values(LeaseDatePreset).includes(preset) ? preset : null;
};

const rehydrateLeaseDurations = (durations: LeaseDurationOption[] | undefined): LeaseDurationOption[] =>
  (durations ?? []).filter((duration) => Object.values(LeaseDurationOption).includes(duration));

const rehydrateCommunicationGroupBy = (
  groupBy: CommunicationGroupByOption | null | undefined
): CommunicationGroupByOption | null => {
  if (isNil(groupBy)) {
    return null;
  }
  return Object.values(CommunicationGroupByOption).includes(groupBy) ? groupBy : null;
};

const rehydrateLeaseGroupBy = (groupBy: LeaseGroupByOption | null | undefined): LeaseGroupByOption | null => {
  if (isNil(groupBy)) {
    return null;
  }
  return Object.values(LeaseGroupByOption).includes(groupBy) ? groupBy : null;
};

const rehydrateTicketGroupBy = (groupBy: TicketGroupByOption | null | undefined): TicketGroupByOption | null => {
  if (isNil(groupBy)) {
    return null;
  }
  return Object.values(TicketGroupByOption).includes(groupBy) ? groupBy : null;
};

const rehydrateLeaseDateFilter = (dateFilter: LeaseDateFilter | undefined): LeaseDateFilter => {
  if (isNil(dateFilter)) {
    return { preset: null };
  }
  return {
    preset: rehydrateLeaseDatePreset(dateFilter.preset),
    from: rehydrateCalendarDate(dateFilter.from) ?? undefined,
    to: rehydrateCalendarDate(dateFilter.to) ?? undefined,
  };
};

const mergeLeaseFilters = <T extends LeasesCommonFilters>(defaultFilters: T, cachedFilters: T): T => ({
  ...defaultFilters,
  ...cachedFilters,
  filters: (cachedFilters.filters ?? []).filter(({ name }) => name !== REMOVED_LEASE_CLASSIC_FILTER_NAME),
  startDateFilter: rehydrateLeaseDateFilter(cachedFilters.startDateFilter),
  durations: rehydrateLeaseDurations(cachedFilters.durations),
  groupBy: rehydrateLeaseGroupBy(cachedFilters.groupBy),
});

const mergeLeasesActiveFilters = (cachedFilters: LeasesActiveFilters): LeasesActiveFilters => ({
  ...mergeLeaseFilters(DEFAULT_LEASES_ACTIVE_FILTERS, cachedFilters),
  endingInFromTo: rehydrateFilterFromTo(cachedFilters.endingInFromTo),
});

const mergeLeasesArchivedFilters = (cachedFilters: LeasesArchivedFilters): LeasesArchivedFilters => ({
  ...mergeLeaseFilters(DEFAULT_LEASES_ARCHIVED_FILTERS, cachedFilters),
  endedSinceFromTo: rehydrateFilterFromTo(cachedFilters.endedSinceFromTo),
});

export interface LeasesActiveFilters extends LeasesCommonFilters {
  endingInFromTo: FilterFromTo | null;
  balanceLatePaymentMonth: number;
  balanceDept: number;
  overdueOnly: boolean;
  priceChangeToValidate: boolean;
  mandateStatus: string[] | null;
}

export type LeasesDraftsFilters = LeasesCommonFilters;

export interface LeasesArchivedFilters extends LeasesCommonFilters {
  endedSinceFromTo: FilterFromTo | null;
  balanceDept: number;
  overdueOnly: boolean;
}

export enum PriceChangeApplicationPeriod {
  PAST = 'PAST',
  CURRENT_MONTH = 'CURRENT_MONTH',
  NEXT_THREE_MONTHS = 'NEXT_THREE_MONTHS',
}

export interface LeasesIndexationsFilters extends CommonFilter, SearchFilter {
  types: LeasePriceHistoryType[];
  applicationPeriod: PriceChangeApplicationPeriod | null;
}

const rehydrateApplicationPeriod = (
  period: PriceChangeApplicationPeriod | null | undefined
): PriceChangeApplicationPeriod | null => {
  if (isNil(period)) {
    return null;
  }
  return Object.values(PriceChangeApplicationPeriod).includes(period) ? period : null;
};

const mergeLeasesIndexationsFilters = (cachedFilters: LeasesIndexationsFilters): LeasesIndexationsFilters => ({
  ...DEFAULT_LEASES_INDEXATIONS_FILTERS,
  ...cachedFilters,
  types: (cachedFilters.types ?? []).filter((type) => Object.values(LeasePriceHistoryType).includes(type)),
  applicationPeriod: rehydrateApplicationPeriod(cachedFilters.applicationPeriod),
});

export interface UnitsFilters extends CommonFilter {
  leaseFilterOwnUse: boolean;
  leaseFilterRenovation: boolean;
  leaseFilterRented: boolean;
  leaseFilterVacant: boolean;
  leaseFilterFromTo: FilterFromTo | null;
  leaseFilterRentedButVacantFromTo: FilterFromTo | null;
  leaseFilterRentedAfter: boolean;
  balanceFilterLatePaymentMonth: number;
  balanceFilterDept: number;
}

export enum TicketResponsible {
  ADMIN_TO_ME = 'toMe',
  ADMIN_TO_OTHERS = 'toOthers',
  ADMIN_UNASSIGNED = 'unassigned',
}

export enum TicketGroupByOption {
  STATUS = 'STATUS',
  BUILDING = 'BUILDING',
  RESPONSIBLE = 'RESPONSIBLE',
}

export interface TicketsFilters extends CommonFilter, SearchFilter {
  ticketsFromTo: FilterFromTo | null;
  plannedFromTo: FilterFromTo | null;
  responsible: TicketResponsible[];
  groupBy: TicketGroupByOption | null;
}

export interface TechnicsCertificatesFilters extends CommonFilter, SearchFilter {
  showArchived: boolean;
}

export interface TransactionsFilters extends CommonFilter {
  transactionsFromTo: FilterFromTo | null;
  transactionsAmount: FilterMinMax | null;
  transactionsUnassignedOnly: boolean;
}

export interface ChargesFilters extends CommonFilter {
  chargesFromTo: FilterFromTo | null;
  chargesAmount: FilterMinMax | null;
}

export interface StatementsFilters extends CommonFilter, SearchFilter {
  statementsPeriod: FilterFromTo | null;
  statementsSentDate: FilterFromTo | null;
  expandAll: {
    income: boolean;
    charges: boolean;
  };
}

export type RateCardAssignableOwnersFilters = CommonFilter & SearchFilter;
export interface ReconciliationFilters extends CommonFilter {
  hidePaid: boolean;
  grouping?: DocumentGrouping;
  newestFirst?: boolean;
}

export enum InvoiceGroupBy {
  MONTH = 'MONTH',
  STATUS = 'STATUS',
  TYPE = 'TYPE',
  NONE = 'NONE',
}

export enum InvoiceAmountMode {
  PAID_OVER_DUE = 'PAID_OVER_DUE',
  REMAINING = 'REMAINING',
}

export interface LeaseInvoicesDisplayFilters extends CommonFilter {
  groupBy: InvoiceGroupBy;
  amountMode: InvoiceAmountMode;
}

const DEFAULT_LEASE_INVOICES_DISPLAY_FILTERS: LeaseInvoicesDisplayFilters = {
  filters: [],
  groupBy: InvoiceGroupBy.MONTH,
  amountMode: InvoiceAmountMode.PAID_OVER_DUE,
};

export interface FiltersContext {
  unitsFilters: UnitsFilters;
  chargesFilters: ChargesFilters;
  communicationsFilters: CommunicationsFilters;
  contactsFilters: ContactsFilters;
  ticketsFilters: TicketsFilters;
  transactionsFilters: TransactionsFilters;
  tenantStatementsFilters: StatementsFilters;
  ownerStatementsFilters: StatementsFilters;
  rateCardAssignableOwners: RateCardAssignableOwnersFilters;
  propertiesFilters: PropertiesFilters;
  leasesActiveFilters: LeasesActiveFilters;
  leasesDraftsFilters: LeasesDraftsFilters;
  leasesArchivedFilters: LeasesArchivedFilters;
  leasesIndexationsFilters: LeasesIndexationsFilters;
  technicsCertificatesFilters: TechnicsCertificatesFilters;
  reconciliationFilters: ReconciliationFilters;
  leaseInvoicesDisplayFilters: LeaseInvoicesDisplayFilters;
  managementFiltersFilters: ManagementFiltersFilters;
  updateFilters<T extends CommonFilter>(filterEntity: FilterEntity, variables: T): Promise<void>;
  resetFilters(filterEntity: FilterEntity, subFilter?: string): Promise<void>;
}

export enum FilterEntity {
  UNITS = 'Filters_Units',
  CHARGES = 'Filters_Charges',
  COMMUNICATIONS = 'Filters_Communications',
  CONTACTS = 'Filters_Contacts',
  LEASES_ACTIVE = 'Filters_Leases_Active',
  LEASES_DRAFTS = 'Filters_Leases_Drafts',
  LEASES_ARCHIVED = 'Filters_Leases_Archived',
  LEASES_INDEXATIONS = 'Filters_Leases_Indexations',
  OWNER_STATEMENTS = 'Filters_Owner_Statements',
  PROPERTIES = 'Filters_Properties',
  RATE_CARD_ASSIGNABLE_OWNERS = 'Filters_RateCardAssignableOwners',
  TENANT_STATEMENTS = 'Filters_Tenant_Statements',
  TICKETS = 'Filters_Tickets',
  TRANSACTIONS = 'Filters_Transactions',
  TECHNICS_CERTIFICATES = 'Filters_TechnicsCertificates',
  DOCUMENTS = 'Filters_Documents',
  RECONCILIATION = 'Filters_Reconciliation',
  LEASE_INVOICES_DISPLAY = 'Filters_Lease_Invoices_Display',
  MANAGEMENT_FILTERS_FILTERS = 'Filters_ManagementFiltersFilters',
}
export const FiltersContext = React.createContext<FiltersContext | null>(null);

export const DEFAULT_PROPERTIES_FILTERS: PropertiesFilters = {
  filters: [],
  search: null,
  rooms: null,
  budget: null,
  availabilityFromTo: { from: null, to: null },
  availabilitySelected: null,
  allBuildingsOpen: false,
  collapsedGroupIds: [],
  unitsOpenByBuildingId: {},
};
const DEFAULT_LEASES_COMMON_FILTERS: LeasesCommonFilters = {
  filters: [],
  search: null,
  startDateFilter: { preset: null },
  durations: [],
  subletting: null,
  groupBy: null,
};
export const DEFAULT_LEASES_ACTIVE_FILTERS: LeasesActiveFilters = {
  ...DEFAULT_LEASES_COMMON_FILTERS,
  endingInFromTo: null,
  balanceLatePaymentMonth: 0,
  balanceDept: 0,
  overdueOnly: false,
  priceChangeToValidate: false,
  mandateStatus: null,
};
export const DEFAULT_LEASES_DRAFTS_FILTERS: LeasesDraftsFilters = { ...DEFAULT_LEASES_COMMON_FILTERS };
export const DEFAULT_LEASES_ARCHIVED_FILTERS: LeasesArchivedFilters = {
  ...DEFAULT_LEASES_COMMON_FILTERS,
  balanceDept: 0,
  overdueOnly: false,
  endedSinceFromTo: null,
};
export const DEFAULT_LEASES_INDEXATIONS_FILTERS: LeasesIndexationsFilters = {
  filters: [],
  search: null,
  types: [],
  applicationPeriod: null,
};
export const DEFAULT_TICKETS_FILTERS: TicketsFilters = {
  filters: [],
  ticketsFromTo: null,
  plannedFromTo: null,
  responsible: [],
  search: null,
  groupBy: null,
};
export const DEFAULT_COMMUNICATIONS_FILTERS: CommunicationsFilters = {
  filters: [],
  communicationsFromTo: null,
  search: null,
  groupBy: null,
};
export const DEFAULT_TECHNICS_CERTIFICATES_FILTERS: TechnicsCertificatesFilters = {
  filters: [],
  search: null,
  showArchived: false,
};
export const DEFAULT_PROPERTIES_VACANT_WITHIN_MONTHS = 4;

export const DEFAULT_STATEMENTS_FILTERS: StatementsFilters = {
  filters: [],
  search: null,
  statementsPeriod: null,
  statementsSentDate: null,
  expandAll: {
    income: false,
    charges: false,
  },
};
export const FiltersContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const defaultUnitsFilters = {
    filters: [],
    leaseFilterOwnUse: true,
    leaseFilterRenovation: true,
    leaseFilterRented: true,
    leaseFilterVacant: true,
    leaseFilterFromTo: null,
    leaseFilterRentedButVacantFromTo: null,
    leaseFilterRentedAfter: false,
    balanceFilterLatePaymentMonth: 0,
    balanceFilterDept: 0,
  };
  const defaultContactsFilters: ContactsFilters = { filters: [], search: null, contactTypes: [] };
  const defaultTransactionsFilters: TransactionsFilters = {
    filters: [],
    transactionsFromTo: null,
    transactionsAmount: null,
    transactionsUnassignedOnly: false,
  };
  const defaultChargesFilters = {
    filters: [],
    chargesFromTo: null,
    chargesAmount: null,
  };
  const defaultRateCardAssignableOwners: RateCardAssignableOwnersFilters = { filters: [], search: null };
  const defaultReconciliationFilters = { filters: [], hidePaid: true };
  const defaultManagementFiltersFilters = {
    filters: [],
    search: null,
    showUnassignedEntities: false,
  };

  const [communicationsFilters, setCommunicationsFilters] =
    useState<CommunicationsFilters>(DEFAULT_COMMUNICATIONS_FILTERS);
  const [contactsFilters, setContactsFilters] = useState<ContactsFilters>(defaultContactsFilters);
  const [unitsFilters, setUnitsFilters] = useState<UnitsFilters>(defaultUnitsFilters);
  const [ticketsFilters, setTicketsFilters] = useState<TicketsFilters>(DEFAULT_TICKETS_FILTERS);
  const [technicsCertificatesFilters, setTechnicsCertificatesFilters] = useState<TechnicsCertificatesFilters>(
    DEFAULT_TECHNICS_CERTIFICATES_FILTERS
  );
  const [transactionsFilters, setTransactionsFilters] = useState<TransactionsFilters>(defaultTransactionsFilters);
  const [chargesFilters, setChargesFilters] = useState<ChargesFilters>(defaultChargesFilters);
  const [tenantStatementsFilters, setTenantStatementsFilters] = useState<StatementsFilters>(DEFAULT_STATEMENTS_FILTERS);
  const [ownerStatementsFilters, setOwnerStatementsFilters] = useState<StatementsFilters>(DEFAULT_STATEMENTS_FILTERS);
  const [rateCardAssignableOwners, setRateCardAssignableOwners] = useState<RateCardAssignableOwnersFilters>(
    defaultRateCardAssignableOwners
  );
  const [propertiesFilters, setPropertiesFilters] = useState<PropertiesFilters>(DEFAULT_PROPERTIES_FILTERS);
  const [leasesActiveFilters, setLeasesActiveFilters] = useState<LeasesActiveFilters>(DEFAULT_LEASES_ACTIVE_FILTERS);
  const [leasesDraftsFilters, setLeasesDraftsFilters] = useState<LeasesDraftsFilters>(DEFAULT_LEASES_DRAFTS_FILTERS);
  const [leasesArchivedFilters, setLeasesArchivedFilters] = useState<LeasesArchivedFilters>(
    DEFAULT_LEASES_ARCHIVED_FILTERS
  );
  const [leasesIndexationsFilters, setLeasesIndexationsFilters] = useState<LeasesIndexationsFilters>(
    DEFAULT_LEASES_INDEXATIONS_FILTERS
  );
  const [reconciliationFilters, setReconciliationFilters] =
    useState<ReconciliationFilters>(defaultReconciliationFilters);
  const [leaseInvoicesDisplayFilters, setLeaseInvoicesDisplayFilters] = useState<LeaseInvoicesDisplayFilters>(
    DEFAULT_LEASE_INVOICES_DISPLAY_FILTERS
  );
  const [managementFiltersFilters, setManagementFiltersFilters] = useState<ManagementFiltersFilters>(
    defaultManagementFiltersFilters
  );

  const getFilters = async (filterEntity: FilterEntity) => {
    return await getCacheItem(filterEntity);
  };

  const updateFilters = async <T extends CommonFilter>(filterEntity: FilterEntity, variables: T) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let setFct: (filters: any) => void = () => {};
    switch (filterEntity) {
      case FilterEntity.COMMUNICATIONS:
        setFct = setCommunicationsFilters;
        break;
      case FilterEntity.CONTACTS:
        setFct = setContactsFilters;
        break;
      case FilterEntity.UNITS:
        setFct = setUnitsFilters;
        break;
      case FilterEntity.TICKETS:
        setFct = setTicketsFilters;
        break;
      case FilterEntity.TECHNICS_CERTIFICATES:
        setFct = setTechnicsCertificatesFilters;
        break;
      case FilterEntity.TRANSACTIONS:
        setFct = setTransactionsFilters;
        break;
      case FilterEntity.CHARGES:
        setFct = setChargesFilters;
        break;
      case FilterEntity.TENANT_STATEMENTS:
        setFct = setTenantStatementsFilters;
        break;
      case FilterEntity.OWNER_STATEMENTS:
        setFct = setOwnerStatementsFilters;
        break;
      case FilterEntity.RATE_CARD_ASSIGNABLE_OWNERS:
        setFct = setRateCardAssignableOwners;
        break;
      case FilterEntity.PROPERTIES:
        setFct = setPropertiesFilters;
        break;
      case FilterEntity.LEASES_ACTIVE:
        setFct = setLeasesActiveFilters;
        break;
      case FilterEntity.LEASES_DRAFTS:
        setFct = setLeasesDraftsFilters;
        break;
      case FilterEntity.LEASES_ARCHIVED:
        setFct = setLeasesArchivedFilters;
        break;
      case FilterEntity.LEASES_INDEXATIONS:
        setFct = setLeasesIndexationsFilters;
        break;
      case FilterEntity.RECONCILIATION:
        setFct = setReconciliationFilters;
        break;
      case FilterEntity.LEASE_INVOICES_DISPLAY:
        setFct = setLeaseInvoicesDisplayFilters;
        break;
      case FilterEntity.MANAGEMENT_FILTERS_FILTERS:
        setFct = setManagementFiltersFilters;
        break;
    }
    await setCacheItem(filterEntity, variables);
    setFct(variables);
  };

  const resetFilters = async (filterEntity: FilterEntity) => {
    let defaultValue;
    switch (filterEntity) {
      case FilterEntity.COMMUNICATIONS:
        defaultValue = DEFAULT_COMMUNICATIONS_FILTERS;
        break;
      case FilterEntity.CONTACTS:
        defaultValue = defaultContactsFilters;
        break;
      case FilterEntity.UNITS:
        defaultValue = defaultUnitsFilters;
        break;
      case FilterEntity.TICKETS:
        defaultValue = DEFAULT_TICKETS_FILTERS;
        break;
      case FilterEntity.TECHNICS_CERTIFICATES:
        defaultValue = DEFAULT_TECHNICS_CERTIFICATES_FILTERS;
        break;
      case FilterEntity.TRANSACTIONS:
        defaultValue = defaultTransactionsFilters;
        break;
      case FilterEntity.CHARGES:
        defaultValue = defaultChargesFilters;
        break;
      case FilterEntity.OWNER_STATEMENTS:
      case FilterEntity.TENANT_STATEMENTS:
        defaultValue = DEFAULT_STATEMENTS_FILTERS;
        break;
      case FilterEntity.RATE_CARD_ASSIGNABLE_OWNERS:
        defaultValue = defaultRateCardAssignableOwners;
        break;
      case FilterEntity.PROPERTIES:
        defaultValue = DEFAULT_PROPERTIES_FILTERS;
        break;
      case FilterEntity.LEASES_ACTIVE:
        defaultValue = DEFAULT_LEASES_ACTIVE_FILTERS;
        break;
      case FilterEntity.LEASES_DRAFTS:
        defaultValue = DEFAULT_LEASES_DRAFTS_FILTERS;
        break;
      case FilterEntity.LEASES_ARCHIVED:
        defaultValue = DEFAULT_LEASES_ARCHIVED_FILTERS;
        break;
      case FilterEntity.LEASES_INDEXATIONS:
        defaultValue = DEFAULT_LEASES_INDEXATIONS_FILTERS;
        break;
      case FilterEntity.MANAGEMENT_FILTERS_FILTERS:
        defaultValue = defaultManagementFiltersFilters;
        break;
    }
    if (defaultValue) {
      await updateFilters(filterEntity, defaultValue);
    }
  };

  useEffect(() => {
    const initializeFilters = async () => {
      const communicationsFilters = await getFilters(FilterEntity.COMMUNICATIONS);
      if (!isNil(communicationsFilters)) {
        setCommunicationsFilters({
          ...DEFAULT_COMMUNICATIONS_FILTERS,
          ...communicationsFilters,
          groupBy: rehydrateCommunicationGroupBy(communicationsFilters.groupBy),
        });
      }
      const contactsFilters = await getFilters(FilterEntity.CONTACTS);
      if (!isNil(contactsFilters)) {
        setContactsFilters(contactsFilters);
      }
      const unitsFilters = await getFilters(FilterEntity.UNITS);
      if (!isNil(unitsFilters)) {
        setUnitsFilters(unitsFilters);
      }
      const ticketsFilters = await getFilters(FilterEntity.TICKETS);
      if (!isNil(ticketsFilters)) {
        const rawResponsible = ticketsFilters.responsible ?? [];
        const responsible = Array.isArray(rawResponsible)
          ? rawResponsible.filter((responsibleValue): responsibleValue is TicketResponsible =>
              Object.values(TicketResponsible).includes(responsibleValue)
            )
          : [];
        setTicketsFilters({
          search: null,
          ...ticketsFilters,
          responsible,
          groupBy: rehydrateTicketGroupBy(ticketsFilters.groupBy),
        });
      }
      const technicsCertificatesFilters = await getFilters(FilterEntity.TECHNICS_CERTIFICATES);
      if (!isNil(technicsCertificatesFilters)) {
        setTechnicsCertificatesFilters({ ...DEFAULT_TECHNICS_CERTIFICATES_FILTERS, ...technicsCertificatesFilters });
      }
      const transactionsFilters = await getFilters(FilterEntity.TRANSACTIONS);
      if (!isNil(transactionsFilters)) {
        setTransactionsFilters(transactionsFilters);
      }
      const chargesFilters = await getFilters(FilterEntity.CHARGES);
      if (!isNil(chargesFilters)) {
        setChargesFilters(chargesFilters);
      }
      const tenantStatementsFilters = await getFilters(FilterEntity.TENANT_STATEMENTS);
      if (!isNil(tenantStatementsFilters)) {
        setTenantStatementsFilters(tenantStatementsFilters);
      }
      const ownerStatementsFilters = await getFilters(FilterEntity.OWNER_STATEMENTS);
      if (!isNil(ownerStatementsFilters)) {
        setOwnerStatementsFilters(ownerStatementsFilters);
      }
      const rateCardAssignableOwners = await getFilters(FilterEntity.RATE_CARD_ASSIGNABLE_OWNERS);
      if (!isNil(rateCardAssignableOwners)) {
        setRateCardAssignableOwners(rateCardAssignableOwners);
      }
      const propertiesFilters = await getFilters(FilterEntity.PROPERTIES);
      if (!isNil(propertiesFilters)) {
        setPropertiesFilters({ ...DEFAULT_PROPERTIES_FILTERS, ...propertiesFilters });
      }
      const leasesActiveFilters = await getFilters(FilterEntity.LEASES_ACTIVE);
      if (!isNil(leasesActiveFilters)) {
        setLeasesActiveFilters(mergeLeasesActiveFilters(leasesActiveFilters));
      }
      const leasesDraftsFilters = await getFilters(FilterEntity.LEASES_DRAFTS);
      if (!isNil(leasesDraftsFilters)) {
        setLeasesDraftsFilters(mergeLeaseFilters(DEFAULT_LEASES_DRAFTS_FILTERS, leasesDraftsFilters));
      }
      const leasesArchivedFilters = await getFilters(FilterEntity.LEASES_ARCHIVED);
      if (!isNil(leasesArchivedFilters)) {
        setLeasesArchivedFilters(mergeLeasesArchivedFilters(leasesArchivedFilters));
      }
      const leasesIndexationsFilters = await getFilters(FilterEntity.LEASES_INDEXATIONS);
      if (!isNil(leasesIndexationsFilters)) {
        setLeasesIndexationsFilters(mergeLeasesIndexationsFilters(leasesIndexationsFilters));
      }
      const reconciliationFilters = await getFilters(FilterEntity.RECONCILIATION);
      if (!isNil(reconciliationFilters)) {
        setReconciliationFilters(reconciliationFilters);
      }
      const leaseInvoicesDisplayFilters = await getFilters(FilterEntity.LEASE_INVOICES_DISPLAY);
      if (!isNil(leaseInvoicesDisplayFilters)) {
        setLeaseInvoicesDisplayFilters({ ...DEFAULT_LEASE_INVOICES_DISPLAY_FILTERS, ...leaseInvoicesDisplayFilters });
      }
      const managementFiltersFilters = await getFilters(FilterEntity.MANAGEMENT_FILTERS_FILTERS);
      if (!isNil(managementFiltersFilters)) {
        setManagementFiltersFilters(managementFiltersFilters);
      }
    };
    initializeFilters();
  }, []);

  const values = React.useMemo(
    () => ({
      chargesFilters,
      communicationsFilters,
      contactsFilters,
      unitsFilters,
      ticketsFilters,
      technicsCertificatesFilters,
      transactionsFilters,
      tenantStatementsFilters,
      ownerStatementsFilters,
      rateCardAssignableOwners,
      propertiesFilters,
      leasesActiveFilters,
      leasesDraftsFilters,
      leasesArchivedFilters,
      leasesIndexationsFilters,
      managementFiltersFilters,
      updateFilters,
      resetFilters,
      reconciliationFilters,
      leaseInvoicesDisplayFilters,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      chargesFilters,
      communicationsFilters,
      contactsFilters,
      unitsFilters,
      ticketsFilters,
      technicsCertificatesFilters,
      transactionsFilters,
      tenantStatementsFilters,
      ownerStatementsFilters,
      rateCardAssignableOwners,
      propertiesFilters,
      leasesActiveFilters,
      leasesDraftsFilters,
      leasesArchivedFilters,
      leasesIndexationsFilters,
      reconciliationFilters,
      leaseInvoicesDisplayFilters,
      managementFiltersFilters,
    ]
  );
  return <FiltersContext.Provider value={values}>{children}</FiltersContext.Provider>;
};

export const useFilters = (): FiltersContext => {
  const context = useContext<FiltersContext | null>(FiltersContext);

  if (context === undefined) {
    throw new Error('`useFilters` hook must be used within a `FiltersContextProvider` component');
  }
  return context as FiltersContext;
};
